import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import type { MyProfileViewModel, TooltipModelMaybe } from 'autogen/swagger/all';
import { LogIn, MyProfile as MyProfileTrans } from 'autogen/translation-keys/trans-website-shared';
import InlineButton from 'components/generic/InlineButton';
import Icon from 'components/Icon';
import { SessionMenuAnchorItem, SessionMenuItem } from 'components/SessionMenu/components/SessionMenuItem';
import Tooltip from 'components/shared/Tooltip/Tooltip';
import TooltipNotificationAtom from 'components/shared/TooltipNotification/shared/recoil/atom';
import { useDynamicStorage } from 'hooks/storage';
import useIsAuthenticated from 'hooks/user/useIsAuthenticated';
import useLoginLink from 'hooks/user/useLoginLink';
import loadHtmlModalFunc from 'modules/modal/helpers';
import Default from 'modules/modal/layouts';
import { bindComponents } from 'modules/modalLink';
import { GA4Tracking } from 'modules/tracking/GA4';
import { useRecoilValue } from 'recoil';
import { HeaderItemText } from './shared/styles';

const RelativeContainer = styled.div`
    width: 20px;
    height: 20px;
    position: relative;

    &:after {
        content: '';
        position: absolute;
        right: 0;
        top: 0;
        background-color: #f23939;
        width: 8px;
        height: 8px;
        display: block;
        border-radius: 100%;
    }

    // used to fix alignment of text
    svg {
        width: inherit;
        height: inherit;
    }
`;

const StyledTooltip = styled(Tooltip)`
    && {
        max-width: 250px;
    }
`;

const StyledInlineButton = styled(InlineButton)`
    font-weight: bold;
`;

function MyProfileLoginModal({ model: { loginRequest } }: { model: MyProfileViewModel }) {
    const { t } = useTranslation();
    const { loginModal } = useLoginLink();
    const { loginLinkAction } = loginRequest;

    return (
        <SessionMenuItem
            icon={<Icon type="User" size={16} aria-hidden />}
            onClick={async () => {
                const htmlModal = await loadHtmlModalFunc();
                const modal = htmlModal({ layout: { component: (props) => <Default {...props} /> } });
                modal({ url: loginLinkAction.url, onAfterContent: bindComponents });

                GA4Tracking({
                    eventName: 'open_sso_modal',
                    context: 'header',
                    category: 'login'
                });
            }}
        >
            <HeaderItemText>{t(LogIn)}</HeaderItemText>
        </SessionMenuItem>
    );
}

function useLoginUrlTemplate(loginUrlTemplate: string): string {
    const [loginUrl, setLoginUrl] = useState(loginUrlTemplate);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect((): void => {
        const returnUrl = window.encodeURIComponent(window.location.href.substring(window.location.origin.length));

        setLoginUrl(`${loginUrl}&returnurl=${returnUrl}`);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return loginUrl;
}

function MyProfileNotAuthenticated({
    model: { loginRequest },
    tooltip
}: {
    model: MyProfileViewModel;
    tooltip?: TooltipModelMaybe;
}) {
    const { loginLinkAction } = loginRequest;
    const { t } = useTranslation();
    const loginUrlTemplate = useLoginUrlTemplate(loginLinkAction.url ?? '');
    const isOpen = tooltip ? useRecoilValue(TooltipNotificationAtom(tooltip.tooltipType)) : false;
    const isAuthenticated = useIsAuthenticated();
    const [showNudgingStorage, setShowNudgingStorage] = useDynamicStorage(
        'sessionStorage',
        'shouldShowLoginNudgeKey',
        !isAuthenticated
    );

    useEffect(() => {
        if (!isAuthenticated && showNudgingStorage) {
            setShowNudging(true);
        }
    }, [isAuthenticated, showNudgingStorage]);

    const [showNudging, setShowNudging] = useState(false);

    const mutatedLoginLinkAction = { ...loginLinkAction, url: loginUrlTemplate }; // todo niels, what should be happening here?

    return (
        <StyledTooltip
            id={tooltip?.tooltipType}
            enabled={!!tooltip}
            popup
            popupMaxCount={1}
            popupCountStorage="sessionStorage"
            enableHover={false}
            contentRenderer={{
                headline: tooltip?.title,
                body: tooltip?.content || '',
                action: ({ close }) => <StyledInlineButton onClick={close}>{tooltip?.dismissText}</StyledInlineButton>
            }}
        >
            {(props) => (
                <SessionMenuAnchorItem
                    {...props}
                    icon={
                        showNudging ? (
                            <RelativeContainer>
                                <Icon type="User" size={16} aria-hidden />
                            </RelativeContainer>
                        ) : (
                            <Icon type="User" size={16} aria-hidden />
                        )
                    }
                    action={mutatedLoginLinkAction}
                    onClick={() => {
                        setShowNudgingStorage(false);

                        GA4Tracking({
                            eventName: 'open_sso_modal',
                            context: 'header',
                            category: 'login'
                        });
                    }}
                >
                    <HeaderItemText>{t(LogIn)}</HeaderItemText>
                </SessionMenuAnchorItem>
            )}
        </StyledTooltip>
    );
}

function MyProfileAuthenticated({ model: { myProfileLinkAction } }: { model: MyProfileViewModel }) {
    const { t } = useTranslation();

    return (
        <SessionMenuAnchorItem
            icon={<Icon type="User" size={16} aria-hidden />}
            action={myProfileLinkAction}
            onClick={() => {
                GA4Tracking({
                    eventName: 'go_to_my_matas',
                    context: 'header',
                    category: 'navigation',
                    eventParams: [{ param_name: 'link_type', param_value: 'internal' }]
                });
            }}
        >
            <HeaderItemText>{t(MyProfileTrans)}</HeaderItemText>
        </SessionMenuAnchorItem>
    );
}

export default function MyProfile({ model, tooltip }: { model: MyProfileViewModel; tooltip?: TooltipModelMaybe }) {
    const {
        loginRequest: { loginLinkAction }
    } = model;
    const isAuthenticated = useIsAuthenticated();
    const { loginModal } = useLoginLink();

    if (!loginLinkAction.url && loginModal) {
        return <MyProfileLoginModal model={model} />;
    }

    if (!isAuthenticated) {
        return <MyProfileNotAuthenticated model={model} tooltip={tooltip} />;
    }

    return <MyProfileAuthenticated model={model} />;
}
